import { roomInstance } from '@webclient/state/slices/search-session/utils/room-manager';
import {
  resetTempTranscript,
  setDictationState,
  setTempTranscript,
  setTranscript,
  startDictation,
  stopDictation,
} from '@webclient/state/slices/search-widget-dictation';
import { AppDispatch, RootType } from '@webclient/state/store';

import {
  isKrispNoiseFilterSupported,
  KrispNoiseFilter,
} from '@livekit/krisp-noise-filter';
import { DataPacket_Kind, Participant, RoomEvent } from 'livekit-client';
import { LocalAudioTrack } from 'livekit-client';
import { Middleware } from 'redux';

const searchWidgetDictationMiddleware: Middleware = ({
  dispatch,
  getState,
}: {
  dispatch: AppDispatch;
  getState: () => RootType;
}) => {
  function handleRecieveData(
    payload: Uint8Array,
    _participant?: Participant,
    _kind?: DataPacket_Kind,
    topic?: string,
  ) {
    const decoder = new TextDecoder();
    const decodedPayload = decoder.decode(payload);
    const parsedData = JSON.parse(decodedPayload) as {
      text: string;
    };
    const typedTopic = topic as 'stt_temp' | 'stt_sentence';

    if (isKrispNoiseFilterSupported()) {
      const krispProcessor = KrispNoiseFilter();
      krispProcessor.setEnabled(true);
    } else {
      console.error(
        'Krisp noise filter is currently not supported on this browser',
      );
    }
    if (typedTopic === 'stt_temp') {
      dispatch(setTempTranscript(parsedData.text));
    }

    if (typedTopic === 'stt_sentence') {
      dispatch(setTranscript(parsedData.text));
      dispatch(resetTempTranscript());
    }
  }
  return (next) => async (action) => {
    if (startDictation.match(action)) {
      const room = roomInstance.get();
      room?.off(RoomEvent.DataReceived, handleRecieveData);

      room?.on(RoomEvent.DataReceived, handleRecieveData);
      const audioPublication = room?.localParticipant
        .getTrackPublications()
        .find((pub) => pub.track?.source === 'microphone');

      if (
        audioPublication &&
        audioPublication.track instanceof LocalAudioTrack
      ) {
        // Resume if track already exists (mic used previously)
        room?.localParticipant.setMicrophoneEnabled(true);
        audioPublication.track.resumeUpstream();
      } else {
        // No track yet (we connected without audio). Enable mic now, LiveKit
        // will create and publish a new LocalAudioTrack.
        room?.localParticipant.setMicrophoneEnabled(true);
      }

      room?.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ modules: ['stt'] })),
        {
          reliable: true,
          topic: 'add_modules',
        },
      );

      dispatch(setDictationState(true));
    }

    if (stopDictation.match(action)) {
      const room = roomInstance.get();
      dispatch(resetTempTranscript());
      room?.off(RoomEvent.DataReceived, handleRecieveData);
      const audioPublication = room?.localParticipant
        .getTrackPublications()
        .find((pub) => pub.track?.source === 'microphone');

      if (
        audioPublication &&
        audioPublication.track instanceof LocalAudioTrack
      ) {
        room?.localParticipant.setMicrophoneEnabled(false);
        audioPublication.track.stop();
      }

      room?.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ modules: ['stt'] })),
        {
          reliable: true,
          topic: 'remove_modules',
        },
      );
      dispatch(setDictationState(false));
    }

    next(action);
  };
};

export default searchWidgetDictationMiddleware;
